import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { Room } from "@/types/Room";
import { Lock } from "lucide-react";

interface RoomFilterSelectProps {
  availableRooms: Room[];
  selectedRooms: Room[];
  onSelectionChange: (rooms: Room[]) => void;
  idPrefix?: string;
}

export const RoomFilterSelect = ({
  availableRooms,
  selectedRooms,
  onSelectionChange,
  idPrefix = "room-filter",
}: RoomFilterSelectProps) => {
  const isRoomShuttered = useMemo(() => {
    return (room: Room): boolean => {
      if (!room.shutterUntil) return false;
      const shutterDate = new Date(room.shutterUntil);
      return shutterDate > new Date();
    };
  }, []);

  return (
    <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto">
      <div className="flex items-center gap-2 px-2 py-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded cursor-pointer">
        <Checkbox
          id={`${idPrefix}-all-rooms-checkbox`}
          checked={selectedRooms.length === availableRooms.length}
          onCheckedChange={(checked) => {
            if (checked) {
              onSelectionChange(availableRooms);
            } else {
              onSelectionChange([]);
            }
          }}
        />
        <Label htmlFor={`${idPrefix}-all-rooms-checkbox`} className="flex-1 cursor-pointer font-semibold">
          All rooms
        </Label>
      </div>
      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
      {availableRooms.map((room) => {
        const shuttered = isRoomShuttered(room);
        return (
          <div
            key={room.id}
            className={`flex items-center gap-2 px-2 py-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded cursor-pointer ${shuttered ? "opacity-60" : ""}`}
          >
            <Checkbox
              id={`${idPrefix}-room-${room.id}`}
              checked={selectedRooms.some((r) => r.id === room.id)}
              onCheckedChange={(checked) => {
                if (checked) {
                  // Add room while maintaining order from availableRooms
                  const newSelected = [...selectedRooms, room];
                  onSelectionChange(
                    newSelected.sort((a, b) => {
                      const indexA = availableRooms.findIndex((r) => r.id === a.id) ?? 999;
                      const indexB = availableRooms.findIndex((r) => r.id === b.id) ?? 999;
                      return indexA - indexB;
                    }),
                  );
                } else {
                  onSelectionChange(selectedRooms.filter((r) => r.id !== room.id));
                }
              }}
            />
            <Label htmlFor={`${idPrefix}-room-${room.id}`} className="flex-1 cursor-pointer">
              <span className={shuttered ? "text-gray-500 dark:text-gray-400" : ""}>{room.name}</span>
              {shuttered && (
                <Badge variant="secondary" className="ml-2 text-xs bg-red-100 text-red-700">
                  <Lock className="h-3 w-3 mr-1" />
                  Closed
                </Badge>
              )}
            </Label>
          </div>
        );
      })}
    </div>
  );
};
